import { supabase } from '../lib/supabase';

/**
 * Content Moderation Service
 * Handles content moderation queue, flagging, company verification, and automated screening
 */

export const contentModerationService = {
  /**
   * Get moderation queue with filtering
   */
  async getModerationQueue(filters = {}) {
    try {
      let query = supabase
        ?.from('moderation_queue')
        ?.select(`
          *,
          submitted_by:user_profiles!moderation_queue_submitted_by_fkey (
            id,
            full_name,
            email
          ),
          assigned_to:admin_employees!moderation_queue_assigned_to_fkey (
            id,
            full_name,
            email,
            role
          )
        `)
        ?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.priority) {
        query = query?.eq('priority', filters?.priority);
      }

      if (filters?.contentType) {
        query = query?.eq('content_type', filters?.contentType);
      }

      if (filters?.assignedTo) {
        query = query?.eq('assigned_to', filters?.assignedTo);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(item => ({
        id: item?.id,
        contentType: item?.content_type,
        contentId: item?.content_id,
        status: item?.status,
        priority: item?.priority,
        submittedBy: item?.submitted_by ? {
          id: item?.submitted_by?.id,
          fullName: item?.submitted_by?.full_name,
          email: item?.submitted_by?.email
        } : null,
        assignedTo: item?.assigned_to ? {
          id: item?.assigned_to?.id,
          fullName: item?.assigned_to?.full_name,
          email: item?.assigned_to?.email,
          role: item?.assigned_to?.role
        } : null,
        automatedScore: item?.automated_score,
        automatedFlags: item?.automated_flags,
        reviewNotes: item?.review_notes,
        reviewedAt: item?.reviewed_at,
        createdAt: item?.created_at,
        updatedAt: item?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching moderation queue:', error);
      throw error;
    }
  },

  /**
   * Get single moderation queue item with content details
   */
  async getModerationItem(itemId) {
    try {
      const { data, error } = await supabase
        ?.from('moderation_queue')
        ?.select(`
          *,
          submitted_by:user_profiles!moderation_queue_submitted_by_fkey (
            id,
            full_name,
            email,
            avatar_url
          ),
          assigned_to:admin_employees!moderation_queue_assigned_to_fkey (
            id,
            full_name,
            email,
            role
          )
        `)
        ?.eq('id', itemId)
        ?.single();

      if (error) throw error;

      // Fetch content details based on content type
      let contentDetails = null;
      if (data?.content_type === 'job_posting') {
        const { data: jobData } = await supabase
          ?.from('jobs')
          ?.select('*, companies(name, logo_url)')
          ?.eq('id', data?.content_id)
          ?.single();
        contentDetails = jobData;
      }

      return {
        id: data?.id,
        contentType: data?.content_type,
        contentId: data?.content_id,
        status: data?.status,
        priority: data?.priority,
        submittedBy: data?.submitted_by,
        assignedTo: data?.assigned_to,
        automatedScore: data?.automated_score,
        automatedFlags: data?.automated_flags,
        reviewNotes: data?.review_notes,
        reviewedAt: data?.reviewed_at,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        contentDetails
      };
    } catch (error) {
      console.error('Error fetching moderation item:', error);
      throw error;
    }
  },

  /**
   * Approve content
   */
  async approveContent(queueId, reviewNotes = '') {
    try {
      const { data, error } = await supabase?.rpc('approve_content', {
        p_queue_id: queueId,
        p_review_notes: reviewNotes
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error approving content:', error);
      throw error;
    }
  },

  /**
   * Reject content
   */
  async rejectContent(queueId, rejectionReason) {
    try {
      const { data, error } = await supabase?.rpc('reject_content', {
        p_queue_id: queueId,
        p_rejection_reason: rejectionReason
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error rejecting content:', error);
      throw error;
    }
  },

  /**
   * Assign moderation item to admin
   */
  async assignModerationItem(queueId, adminId) {
    try {
      const { data, error } = await supabase
        ?.from('moderation_queue')
        ?.update({
          assigned_to: adminId,
          status: 'under_review',
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', queueId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error assigning moderation item:', error);
      throw error;
    }
  },

  /**
   * Get content flags
   */
  async getContentFlags(filters = {}) {
    try {
      let query = supabase
        ?.from('content_flags')
        ?.select(`
          *,
          flagged_by:user_profiles!content_flags_flagged_by_fkey (
            id,
            full_name,
            email
          ),
          resolved_by:admin_employees!content_flags_resolved_by_fkey (
            id,
            full_name,
            email
          )
        `)
        ?.order('created_at', { ascending: false });

      if (filters?.resolved !== undefined) {
        query = query?.eq('resolved', filters?.resolved);
      }

      if (filters?.contentType) {
        query = query?.eq('content_type', filters?.contentType);
      }

      if (filters?.flagReason) {
        query = query?.eq('flag_reason', filters?.flagReason);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(flag => ({
        id: flag?.id,
        contentType: flag?.content_type,
        contentId: flag?.content_id,
        flagReason: flag?.flag_reason,
        flagDetails: flag?.flag_details,
        flaggedBy: flag?.flagged_by,
        isAutomated: flag?.is_automated,
        resolved: flag?.resolved,
        resolvedBy: flag?.resolved_by,
        resolvedAt: flag?.resolved_at,
        resolutionAction: flag?.resolution_action,
        createdAt: flag?.created_at
      }));
    } catch (error) {
      console.error('Error fetching content flags:', error);
      throw error;
    }
  },

  /**
   * Flag content
   */
  async flagContent(contentType, contentId, flagReason, flagDetails = '') {
    try {
      const { data, error } = await supabase?.rpc('flag_content', {
        p_content_type: contentType,
        p_content_id: contentId,
        p_flag_reason: flagReason,
        p_flag_details: flagDetails
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error flagging content:', error);
      throw error;
    }
  },

  /**
   * Resolve content flag
   */
  async resolveFlag(flagId, resolutionAction) {
    try {
      const { data, error } = await supabase
        ?.from('content_flags')
        ?.update({
          resolved: true,
          resolved_at: new Date()?.toISOString(),
          resolution_action: resolutionAction
        })
        ?.eq('id', flagId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error resolving flag:', error);
      throw error;
    }
  },

  /**
   * Get company verification requests
   */
  async getVerificationRequests(filters = {}) {
    try {
      let query = supabase
        ?.from('company_verification_requests')
        ?.select(`
          *,
          company:companies!company_verification_requests_company_id_fkey (
            id,
            name,
            slug,
            logo_url,
            website
          ),
          requested_by:user_profiles!company_verification_requests_requested_by_fkey (
            id,
            full_name,
            email
          ),
          assigned_to:admin_employees!company_verification_requests_assigned_to_fkey (
            id,
            full_name,
            email,
            role
          )
        `)
        ?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.assignedTo) {
        query = query?.eq('assigned_to', filters?.assignedTo);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(request => ({
        id: request?.id,
        companyId: request?.company_id,
        company: request?.company,
        status: request?.status,
        requestedBy: request?.requested_by,
        businessName: request?.business_name,
        businessRegistrationNumber: request?.business_registration_number,
        taxId: request?.tax_id,
        businessAddress: request?.business_address,
        verificationDocuments: request?.verification_documents,
        websiteUrl: request?.website_url,
        contactEmail: request?.contact_email,
        contactPhone: request?.contact_phone,
        additionalInfo: request?.additional_info,
        assignedTo: request?.assigned_to,
        reviewNotes: request?.review_notes,
        rejectionReason: request?.rejection_reason,
        verifiedAt: request?.verified_at,
        createdAt: request?.created_at,
        updatedAt: request?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching verification requests:', error);
      throw error;
    }
  },

  /**
   * Verify company
   */
  async verifyCompany(verificationRequestId, verificationBadge = 'verified', reviewNotes = '') {
    try {
      const { data, error } = await supabase?.rpc('verify_company', {
        p_verification_request_id: verificationRequestId,
        p_verification_badge: verificationBadge,
        p_review_notes: reviewNotes
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error verifying company:', error);
      throw error;
    }
  },

  /**
   * Reject company verification
   */
  async rejectCompanyVerification(verificationRequestId, rejectionReason) {
    try {
      const { data, error } = await supabase?.rpc('reject_company_verification', {
        p_verification_request_id: verificationRequestId,
        p_rejection_reason: rejectionReason
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error rejecting company verification:', error);
      throw error;
    }
  },

  /**
   * Get moderation statistics
   */
  async getModerationStatistics() {
    try {
      const { data, error } = await supabase?.rpc('get_moderation_statistics');

      if (error) throw error;

      return {
        pendingItems: data?.pending_items || 0,
        underReview: data?.under_review || 0,
        flaggedItems: data?.flagged_items || 0,
        urgentItems: data?.urgent_items || 0,
        pendingVerifications: data?.pending_verifications || 0,
        unresolvedFlags: data?.unresolved_flags || 0,
        verifiedCompanies: data?.verified_companies || 0,
        approvedToday: data?.approved_today || 0,
        rejectedToday: data?.rejected_today || 0
      };
    } catch (error) {
      console.error('Error fetching moderation statistics:', error);
      throw error;
    }
  },

  /**
   * Get content moderation actions (audit trail)
   */
  async getModerationActions(filters = {}) {
    try {
      let query = supabase
        ?.from('content_moderation_actions')
        ?.select(`
          *,
          performed_by:admin_employees!content_moderation_actions_performed_by_fkey (
            id,
            full_name,
            email,
            role
          )
        `)
        ?.order('performed_at', { ascending: false })
        ?.limit(100);

      if (filters?.contentType) {
        query = query?.eq('content_type', filters?.contentType);
      }

      if (filters?.actionType) {
        query = query?.eq('action_type', filters?.actionType);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(action => ({
        id: action?.id,
        moderationQueueId: action?.moderation_queue_id,
        contentType: action?.content_type,
        contentId: action?.content_id,
        actionType: action?.action_type,
        actionReason: action?.action_reason,
        performedBy: action?.performed_by,
        previousStatus: action?.previous_status,
        newStatus: action?.new_status,
        actionDetails: action?.action_details,
        performedAt: action?.performed_at
      }));
    } catch (error) {
      console.error('Error fetching moderation actions:', error);
      throw error;
    }
  },

  /**
   * Bulk approve content items
   */
  async bulkApprove(queueIds, reviewNotes = '') {
    try {
      const results = await Promise.all(
        queueIds?.map(id => this.approveContent(id, reviewNotes))
      );
      return results;
    } catch (error) {
      console.error('Error bulk approving content:', error);
      throw error;
    }
  },

  /**
   * Bulk reject content items
   */
  async bulkReject(queueIds, rejectionReason) {
    try {
      const results = await Promise.all(
        queueIds?.map(id => this.rejectContent(id, rejectionReason))
      );
      return results;
    } catch (error) {
      console.error('Error bulk rejecting content:', error);
      throw error;
    }
  }
};

export default contentModerationService;